import React, { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Eraser } from "lucide-react";
import { sanitizeHtml } from "../../lib/sanitize";

const TOOLS = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "underline", icon: Underline, label: "Underline" },
  { cmd: "formatBlock:h2", icon: Heading2, label: "Heading" },
  { cmd: "insertUnorderedList", icon: List, label: "Bulleted list" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
  { cmd: "createLink", icon: Link2, label: "Insert link" },
  { cmd: "removeFormat", icon: Eraser, label: "Clear formatting" },
];

/**
 * Lightweight dependency-free rich-text editor (contentEditable) that emits HTML.
 * Used for marketing email bodies; supports click-to-insert merge tags.
 * `mergeTags` accepts either a flat array of tag names, or grouped sections
 * [{ label, tags: [...] }] rendered under labeled headers in a scrollable panel.
 */
export default function RichTextEditor({ value, onChange, placeholder, mergeTags = [], onInsertTag }) {
  const ref = useRef(null);
  const groups = mergeTags.length && typeof mergeTags[0] === "object" ? mergeTags : (mergeTags.length ? [{ label: "", tags: mergeTags }] : []);

  // Sync external value into the DOM only when it diverges and we're not editing
  // (prevents caret jumps while typing; allows template loading to replace content).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = sanitizeHtml(value || "");
    }
  }, [value]);

  const emit = useCallback(() => {
    if (ref.current) onChange(ref.current.innerHTML);
  }, [onChange]);

  const exec = (cmd) => {
    ref.current?.focus();
    if (cmd === "createLink") {
      const url = window.prompt("Link URL", "https://");
      if (url) document.execCommand("createLink", false, url);
    } else if (cmd.startsWith("formatBlock:")) {
      document.execCommand("formatBlock", false, cmd.split(":")[1]);
    } else {
      document.execCommand(cmd, false, null);
    }
    emit();
  };

  const insertTag = (tag) => {
    ref.current?.focus();
    document.execCommand("insertText", false, `{{${tag}}}`);
    emit();
    if (onInsertTag) onInsertTag(tag);
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-800" data-testid="rte-toolbar">
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            title={t.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(t.cmd)}
            data-testid={`rte-${t.cmd.replace(/[:]/g, "-")}`}
            className="h-8 w-8 rounded flex items-center justify-center text-white hover:bg-white/15 active:bg-white/25 transition-colors"
          >
            <t.icon className="h-4 w-4" />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || "Write your message..."}
        data-testid="rte-body"
        className="rte-content min-h-[200px] max-h-[420px] overflow-y-auto px-3 py-2.5 text-[14px] leading-relaxed text-slate-800 focus:outline-none"
      />
      {groups.length > 0 && (
        <div className="border-t border-slate-200 bg-slate-50 max-h-40 overflow-y-auto px-2.5 py-2 space-y-2" data-testid="rte-merge-tags-panel">
          {groups.map((group, gi) => (
            <div key={group.label || gi}>
              {group.label && <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{group.label}</div>}
              <div className="flex flex-wrap gap-1">
                {group.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertTag(tag)}
                    data-testid={`rte-merge-${tag}`}
                    className="h-6 px-2 rounded text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    {`{{${tag}}}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
